"use client";

import { useMemo } from "react";

import { findMatchTeam, type TournamentTeamWithDraftLink } from "../lib/match-team-link";

export type MatchTeamSide = "blue" | "red";

export interface UserMatchSideResult {
  side: MatchTeamSide | null;
  isCaptain: boolean;
  teamACaptainId: string | null;
  teamBCaptainId: string | null;
  teamAMemberIds: string[];
  teamBMemberIds: string[];
}

export interface UseUserMatchSideInput {
  userId: string | null | undefined;
  teamAId: string | null | undefined;
  teamBId: string | null | undefined;
  tournamentTeams:
    | readonly TournamentTeamWithDraftLink[]
    | null
    | undefined;
}

export function computeUserMatchSide(
  input: UseUserMatchSideInput,
): UserMatchSideResult {
  const { userId, teamAId, teamBId, tournamentTeams } = input;
  const empty: UserMatchSideResult = {
    side: null,
    isCaptain: false,
    teamACaptainId: null,
    teamBCaptainId: null,
    teamAMemberIds: [],
    teamBMemberIds: [],
  };

  if (!tournamentTeams?.length) return empty;

  // Через findMatchTeam, а не find по id: в драфт-турнире сетка отдаёт
  // DraftTeam.Id, и прямое сравнение не находило команду вообще — капитан
  // драфт-турнира не опознавался как капитан и не видел своих действий.
  const teamA = findMatchTeam(tournamentTeams, teamAId);
  const teamB = findMatchTeam(tournamentTeams, teamBId);

  const extractMemberIds = (
    team: TournamentTeamWithDraftLink | null,
  ): string[] => {
    if (!team?.members?.length) return [];
    return team.members
      .map((m) => m.userId)
      .filter((id): id is string => Boolean(id));
  };

  const teamACaptainId = teamA?.captainId ?? null;
  const teamBCaptainId = teamB?.captainId ?? null;
  const teamAMemberIds = extractMemberIds(teamA);
  const teamBMemberIds = extractMemberIds(teamB);

  if (!userId) {
    return {
      side: null,
      isCaptain: false,
      teamACaptainId,
      teamBCaptainId,
      teamAMemberIds,
      teamBMemberIds,
    };
  }

  let side: MatchTeamSide | null = null;
  if (userId === teamACaptainId || teamAMemberIds.includes(userId)) {
    side = "blue";
  } else if (userId === teamBCaptainId || teamBMemberIds.includes(userId)) {
    side = "red";
  }

  const isCaptain =
    (side === "blue" && userId === teamACaptainId) ||
    (side === "red" && userId === teamBCaptainId);

  return {
    side,
    isCaptain,
    teamACaptainId,
    teamBCaptainId,
    teamAMemberIds,
    teamBMemberIds,
  };
}

export function useUserMatchSide(
  input: UseUserMatchSideInput,
): UserMatchSideResult {
  const { userId, teamAId, teamBId, tournamentTeams } = input;
  return useMemo(
    () => computeUserMatchSide({ userId, teamAId, teamBId, tournamentTeams }),
    [userId, teamAId, teamBId, tournamentTeams],
  );
}
